import {
  Component,
  DestroyRef,
  inject,
  Inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthGuardService } from '@app/services/auth-guard.service';
import { MediaService } from '@app/services/media.service';
import { OfficeService } from '@app/services/office.service';
import { RecaptchaComponent, ReCaptchaV3Service } from 'ng-recaptcha';
import { Event } from '../../../models/event/Event';
import { EventItem } from '../../../models/event/EventItem';
import { Thema } from '../../../models/event/Thema';
import { Mitarbeiter } from '../../../models/mitarbeiter/Mitarbeiter';
import { ToolsService } from '../../../services/tools.service';

export interface DialogData {
  time: { startTime: string; endTime: string };
  office: { officeid: number; name: string };
  day: string;
  partnerid: string;
  mitarbeiter: Mitarbeiter;
}

@Component({
  selector: 'app-termin',
  templateUrl: './termin.component.html',
  styleUrls: ['./termin.component.scss'],
  standalone: false,
})
export class TerminComponent implements OnInit {
  @ViewChild('captchaElem') captchaElem: RecaptchaComponent;
  termindate: DialogData;
  terminform: FormGroup;
  sitekey: string = '';
  termindata: Event;
  themas = signal<Thema[]>([]);
  arts = signal<EventItem[]>([]);

  destroyRef = inject(DestroyRef);

  constructor(
    private dialogRef: MatDialogRef<TerminComponent>,
    private fb: FormBuilder,
    private toolService: ToolsService,
    protected mediaService: MediaService,
    protected partnerService: AuthGuardService,
    private officeService: OfficeService,
    private recaptchaV3Service: ReCaptchaV3Service,
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
  ) {}

  ngOnInit(): void {
    this.sitekey = this.toolService.getcaptchasitekey();
    this.termindate = this.data;
    this.terminform = this.fb.group({
      salutation: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      thema: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      art: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      firstname: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      lastname: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      tel: new FormControl(null, [
        Validators.required,
        Validators.minLength(2),
      ]),
      email: new FormControl(null, [Validators.required, Validators.email]),
      notiz: new FormControl(null, [
        Validators.maxLength(5000),
        Validators.minLength(0),
      ]),
      floatLabel: 'auto',
    });
    this.getThema();
  }

  async getThema() {
    if (this.termindate?.office?.officeid) {
      this.officeService
        .request(
          {
            officeid: this.termindate.office.officeid,
            partnerid: this.partnerService.getPartnerId(),
          },
          'getofficebyid',
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => {
          if (!res?.error) {
            res.thema = res.thema?.filter(
              (i) => !['vacancy', 'spzt'].includes(i.code),
            );
            this.themas.set(res.thema || []);
            this.arts.set(res.art);
            if (res.thema?.length == 0 || !res.thema) {
              this.themas.set([
                {
                  name: 'Reiseberatung & Buchung',
                  code: 'rb',
                  arts: [
                    {
                      art: 'Vor Ort',
                      duration: 60,
                    },
                    {
                      art: 'Telefonisch',
                      duration: 40,
                    },
                    {
                      art: 'Video-Call',
                      duration: 10,
                    },
                    {
                      art: 'Sonstiges',
                      duration: 20,
                    },
                  ],
                },
                {
                  name: 'Umbuchung & Stornierung',
                  code: 'us',
                  arts: [
                    {
                      art: 'Vor Ort',
                      duration: 60,
                    },
                    {
                      art: 'Telefonisch',
                      duration: 40,
                    },
                    {
                      art: 'Video-Call',
                      duration: 10,
                    },
                    {
                      art: 'Sonstiges',
                      duration: 20,
                    },
                  ],
                },
                {
                  name: 'Abholung Reiseunterlagen',
                  code: 'ar',
                  arts: [
                    {
                      art: 'Vor Ort',
                      duration: 60,
                    },
                    {
                      art: 'Telefonisch',
                      duration: 40,
                    },
                    {
                      art: 'Video-Call',
                      duration: 10,
                    },
                    {
                      art: 'Sonstiges',
                      duration: 20,
                    },
                  ],
                },
                {
                  name: 'Titel festlegen',
                  code: 'tf',
                  arts: [
                    {
                      art: 'Vor Ort',
                      duration: 60,
                    },
                    {
                      art: 'Telefonisch',
                      duration: 40,
                    },
                    {
                      art: 'Video-Call',
                      duration: 10,
                    },
                    {
                      art: 'Sonstiges',
                      duration: 20,
                    },
                  ],
                },
              ]);
            }
          }
        });
    }
  }

  onThemaChange(code) {
    let theme = this.themas().find((i) => i.code == code.value);
    this.arts.set(theme?.arts);
  }

  async onNoClick() {
    this.dialogRef.close();
  }

  async registrierenF() {
    if (this.terminform.valid) {
      this.recaptchaV3Service
        .execute('savetermin')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((token) => this.saveTermin(token));
    } else {
      this.terminform.markAllAsTouched();
    }
  }

  async saveTermin(token) {
    this.termindata = {} as Event;
    this.termindata.description = this.terminform.value.notiz;
    this.termindata.captcha = token;
    this.termindata.art = this.arts().find(
      (i) => i.art == this.terminform.value.art,
    );
    this.termindata.thema = this.themas().find(
      (i) => i.code == this.terminform.value.thema,
    );
    this.termindata.customer = {
      salutation: this.terminform.value.salutation,
      email: this.terminform.value.email,
      partnerid: this.termindate.partnerid,
      firstname: this.terminform.value.firstname,
      lastname: this.terminform.value.lastname,
      tel: this.terminform.value.tel,
    };
    this.dialogRef.close(this.termindata);
  }
}
